import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import Stepper from '@/components/common/organism/Stepper';
import StickyStepStrip from '@/components/common/organism/StickyStepStrip';
import { clinicStepperData, stepperData } from '@/utils/contents';
import ClinicHospitalVerifyStep, { type HospitalStepResult } from './components/ClinicHospitalVerifyStep';
import type { ClinicSignupDone } from './components/DentalSignUpForm';
import SignupTypeSelect from './components/SignupTypeSelect';
import TermsAgreement from './components/TermsAgreement';
import GeneralSignUpForm from './components/GeneralSignUpForm';
import DentalSignUpForm from './components/DentalSignUpForm';
import SignUpComplete from './components/SignUpComplete';
import { TermsId } from './termsContent';
import { signupTypeFromSearch } from '@/pages/AiChat/aiDentistRecruitment';


export interface SocialSignupState {
    socialAccessToken: string;
    socialProvider: 'NAVER' | 'KAKAO' | 'APPLE';
    socialProfile: {
        name: string;
        /** 소셜이 보증(검증)한 이메일. 값이 있으면 입력칸 잠금 + 이메일 인증 면제. */
        email: string;
        /** 소셜이 준 미검증 이메일(Apple). 프리필만 하고 잠그지 않으며 이메일 인증도 그대로 필요하다. */
        suggestedEmail?: string;
        phoneNumber: string;
        gender: string;
        birthday: string;
        birthyear: string;
        nickname: string;
    };
}

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 120px);
    width: 100vw;
    margin-left: calc(-50vw + 50%);

    @media (max-width: 768px) {
        min-height: calc(100vh - 140px);
    }
`;

const PageTitle = styled.h1`
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    text-align: center;
    margin: 0 0 20px;

    @media (max-width: 768px) {
        font-size: 1.25rem;
    }
`;

const BottomSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 40px 60px;
    background: #fff;
    /* 고정 단계 띠가 이 안쪽 여백만큼 넓혀 틀 끝까지 칠한다(StickyStepStrip) */
    --strip-bleed: 40px;

    @media (max-width: 768px) {
        padding: 0 16px 40px;
        --strip-bleed: 16px;
    }
`;

const StepperWrapper = styled.div`
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
    padding-top: 32px;

    @media (max-width: 768px) {
        padding-top: 24px;
    }
`;

const FormWrapper = styled.div`
    width: 100%;
    max-width: 500px;
    margin-top: 36px;
`;

/** 앱 안 주소인지 — `/` 로 시작하고 다른 주인(`//`·`/\`)으로 넘어가지 않는 것만 */
const isAppPath = (path: string): boolean => path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');

/**
 * 가입을 마치고 로그인한 뒤 돌아갈 화면 — 주소의 `returnUrl`(예: 공유받은 AI 대화 [가입하고 이어서 물어보기]).
 * 소셜 가입은 OAuth 왕복으로 주소가 바뀌므로 로그인 화면이 남겨 둔 값(`login_return_url`)을 쓴다.
 */
const readReturnUrl = (search: string, social: boolean): string | null => {
    let value: string | null = new URLSearchParams(search).get('returnUrl');
    if (!value && social) {
        try {
            value = sessionStorage.getItem('login_return_url');
        } catch {
            value = null;
        }
    }
    return value && value !== '/' && isAppPath(value) ? value : null;
};

const Signup = () => {
    const location = useLocation();
    const socialState = (location.state as SocialSignupState | null) ?? null;
    const isSocialSignup: boolean = !!socialState?.socialAccessToken;
    const returnUrl: string | null = readReturnUrl(location.search, isSocialSignup);
    // 주소에 `type=clinic` 이면 병/의원으로 정해 두고 유형 선택을 건너뛴다(AI 대화의 [치과 계정으로 가입하고 이대로 올리기])
    const presetType: 'clinic' | null = isSocialSignup ? null : signupTypeFromSearch(location.search);
    // 1: 유형 선택, 2: 약관 동의, 3: 정보 입력, 4: 완료
    // 소셜(카카오)로 진입하면 유형선택을 건너뛰고 약관(2)부터, 유형은 일반회원 고정.
    const [isStep, setIsStep] = useState(isSocialSignup || presetType ? 2 : 1);
    const [signUpType, setSignUpType] = useState<"user" | "clinic" | "">(isSocialSignup ? "user" : (presetType ?? ""));
    // 2단계(약관 동의)에서 입력받은 동의 상태를 3단계(가입 폼) 제출 시 함께 전송.
    const [agreements, setAgreements] = useState<Record<TermsId, boolean> | undefined>(undefined);
    // 병·의원: 3 병원 인증 → 4 치과 정보 → 5 완료
    const [hospitalStep, setHospitalStep] = useState<HospitalStepResult | null>(null);
    const [clinicDone, setClinicDone] = useState<ClinicSignupDone | null>(null);
    const isClinic: boolean = signUpType === 'clinic';
    const stepperRef = useRef<HTMLDivElement>(null);

    return (
        <PageContainer>

            <BottomSection>
                <StepperWrapper ref={stepperRef}>
                    <PageTitle>회원가입</PageTitle>
                    <Stepper stepData={isClinic ? clinicStepperData : stepperData} target={isStep} />
                </StepperWrapper>
                {/* 스크롤해도 지금 단계가 보이게 — 큰 단계 표시가 위로 사라지면 머리줄 아래 얇은 띠 */}
                <StickyStepStrip stepData={isClinic ? clinicStepperData : stepperData} target={isStep} watchRef={stepperRef} />
                <FormWrapper>
                    {isStep === 1 ? (
                        <SignupTypeSelect
                            onSelect={(type) => { setSignUpType(type); setIsStep(2); }}
                            returnUrl={returnUrl}
                        />
                    ) : isStep === 2 ? (
                        <TermsAgreement
                            onNext={() => setIsStep(3)}
                            onAgreementsChange={setAgreements}
                        />
                    ) : isStep === 3 && signUpType === "user" ? (
                        <GeneralSignUpForm onStep={(s) => setIsStep(s)} socialState={socialState} agreements={agreements} />
                    ) : isStep === 3 && isClinic ? (
                        <ClinicHospitalVerifyStep
                            initial={hospitalStep}
                            onBack={() => setIsStep(2)}
                            onNext={(result) => { setHospitalStep(result); setIsStep(4); }}
                        />
                    ) : isStep === 4 && isClinic && hospitalStep ? (
                        <DentalSignUpForm
                            hospitalStep={hospitalStep}
                            agreements={agreements}
                            onBack={() => setIsStep(3)}
                            onDone={(done) => { setClinicDone(done); setIsStep(5); }}
                        />
                    ) : (isStep === 4 && !isClinic) || (isStep === 5 && isClinic) ? (
                        <SignUpComplete signUpType={signUpType} returnUrl={returnUrl} clinicDone={clinicDone} />
                    ) : null}
                </FormWrapper>
            </BottomSection>
        </PageContainer>
    );
};

export default Signup;
